import { NavLink, useLocation, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { Camera, FolderOpen, GearSix, Stack, House, CloudArrowUp, Pulse, Broadcast, Lock, SignIn, SignOut, User, Desktop } from "@phosphor-icons/react";
import { Queue, drainQueue, onQueueChange, initialQueueCount } from "../lib/offlineQueue";
import { Photos } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const navItems = [
  { to: "/sessions", label: "Sessions", icon: Stack, testid: "nav-sessions" },
  { to: "/setup", label: "Setup", icon: House, testid: "nav-setup" },
  { to: "/camera", label: "Camera", icon: Camera, testid: "nav-camera" },
  { to: "/gallery", label: "Gallery", icon: FolderOpen, testid: "nav-gallery" },
  { to: "/sync", label: "Sync", icon: GearSix, testid: "nav-sync" },
  { to: "/watcher", label: "Watcher", icon: Desktop, testid: "nav-watcher" },
  { to: "/events", label: "Events", icon: Pulse, testid: "nav-events" },
  { to: "/webhooks", label: "Hooks", icon: Broadcast, testid: "nav-webhooks" },
];

export const Shell = ({ children }) => {
  const loc = useLocation();
  const isCamera = loc.pathname === "/camera";
  const [queueCount, setQueueCount] = useState(0);
  const { user, loading: authLoading, logout } = useAuth();

  // Wire global queue state + auto-drain on reconnect.
  useEffect(() => {
    initialQueueCount().then(setQueueCount);
    const off = onQueueChange((c) => setQueueCount(c));
    const onOnline = async () => {
      const c = await Queue.count();
      if (c > 0) {
        await drainQueue((sessionId, payload) => Photos.create(sessionId, payload));
      }
    };
    window.addEventListener("online", onOnline);
    return () => {
      off();
      window.removeEventListener("online", onOnline);
    };
  }, []);

  return (
    <div className="neon-app min-h-[100dvh] flex flex-col bg-white text-black">
      {!isCamera && (
        <header
          className="safe-top border-b-2 border-black bg-white sticky top-0 z-30"
          data-testid="app-header"
        >
          <div className="px-3 sm:px-4 py-3 flex items-center justify-between gap-2 min-w-0">
            <div className="flex items-baseline gap-2">
              <span className="font-display font-black tracking-tighter text-lg sm:text-2xl">
                AIIMAGESUITES
              </span>
              <span className="label-mono text-neutral-500 hidden sm:inline">
                CAPTURE · AIImageSuite.pro
              </span>
            </div>
            <div className="flex items-center gap-1.5 sm:gap-2 min-w-0 flex-wrap justify-end">
              {queueCount > 0 && (
                <span
                  className="chip chip-live flex items-center gap-1"
                  title={`${queueCount} pending offline uploads`}
                  data-testid="header-queue-badge"
                >
                  <CloudArrowUp size={12} weight="bold" />
                  {queueCount}
                </span>
              )}
              <Link to="/dashboard" className="label-mono text-[10px] border-2 border-black px-2 py-1 bg-black text-white hover:bg-white hover:text-black" data-testid="header-suite-dashboard-link">
                ← <span className="hidden sm:inline">DASHBOARD</span><span className="sm:hidden">DASH</span>
              </Link>
              {!authLoading && (
                user ? (
                  <div className="flex items-center gap-1" data-testid="header-user-chip">
                    {user.picture ? (
                      <img
                        src={user.picture}
                        alt=""
                        className="w-6 h-6 border-2 border-black object-cover"
                        data-testid="header-user-avatar"
                      />
                    ) : (
                      <User size={14} weight="bold" />
                    )}
                    <span
                      className="label-mono text-[10px] hidden md:inline max-w-[140px] truncate"
                      title={user.email}
                      data-testid="header-user-email"
                    >
                      {user.name || user.email}
                    </span>
                    <button
                      onClick={logout}
                      data-testid="header-logout-btn"
                      className="label-mono text-[10px] border-2 border-black px-2 py-1 hover:bg-black hover:text-white flex items-center gap-1"
                      title="Sign out"
                    >
                      <SignOut size={10} weight="bold" /> OUT
                    </button>
                  </div>
                ) : (
                  <Link
                    to="/login"
                    data-testid="header-signin-link"
                    title="Sign in with Google"
                    className="label-mono text-[10px] border-2 border-black px-2 py-1 hover:bg-black hover:text-white flex items-center gap-1"
                  >
                    <SignIn size={10} weight="bold" /> SIGN IN
                  </Link>
                )
              )}
              <Link
                to="/admin"
                data-testid="header-ops-link"
                title="Operator cockpit"
                className="label-mono text-[10px] border-2 border-black px-2 py-1 hover:bg-black hover:text-white flex items-center gap-1"
              >
                <Lock size={10} weight="bold" /> OPS
              </Link>
            </div>
          </div>
        </header>
      )}

      <main className={`flex-1 ${isCamera ? "" : "pb-24"}`}>{children}</main>

      {!isCamera && (
        <nav
          className="fixed bottom-0 inset-x-0 z-40 bg-white border-t-2 border-black safe-bottom"
          data-testid="bottom-nav"
        >
          <ul className="grid grid-cols-8">
            {navItems.map(({ to, label, icon: Icon, testid }) => (
              <li key={to} className="border-r border-black last:border-r-0">
                <NavLink
                  to={to}
                  end={to === "/sessions"}
                  data-testid={testid}
                  className={({ isActive }) =>
                    `relative flex flex-col items-center justify-center gap-1 py-3 px-1 transition-colors active:scale-[0.98] ${
                      isActive ? "bg-black text-white" : "bg-white text-black hover:bg-neutral-100"
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      {isActive && <span className="nav-active-indicator" />}
                      <Icon size={22} weight={isActive ? "fill" : "bold"} />
                      <span className="label-mono text-[8px] sm:text-[10px] !tracking-normal sm:!tracking-wider max-w-full truncate">{label}</span>
                    </>
                  )}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
      )}
    </div>
  );
};

export default Shell;
